import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";

import {
  TrendingUp,
  TrendingDown,
  AlertCircle,
  CheckCircle,
  Activity,
  BarChart2,
  Info,
  ChevronDown,
  ChevronUp,
  CreditCard,
} from "lucide-react";
import { SensitiveData } from "@/components/sensitive-data";
import {
  BarChart as RechartsBarChart,
  Bar,
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  LabelList,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import {
  ChartContainer,
  ChartTooltipContent,
  ChartLegendContent,
  ChartTooltip,
  ChartLegend,
  ChartStyle,
} from "@/components/ui/chart";
import type { ChartConfig } from "@/components/ui/chart";

import {
  formatCurrency,
  formatPercent,
  formatDate,
  getVarianceColor,
  calculateEAC,
} from "@/lib/utils";

import { getBudgetHealthThresholds } from '@/lib/budget-health-thresholds-storage'; // Task 5
import { calculateGP, calculateBudgetGP } from '@/lib/gp-calculation'; // Task 23
import { getFinanceSettings } from '@/lib/finance-settings-storage'; // Task 23
import {
  fetchProjectMonthlySummary,
  fetchCostRollupByCategory,
  type MonthlyFinancialSummary,
  type CategoryCostRollup,
} from '@/lib/data-service';

import { useProjectDetailsContext } from "../context";
import type { BurnPoint, HoursBurndownPoint } from "../types";
import type { TimeEntry } from "@/types/timesheet";

interface FixedFeeInvoice {
  id: number;
  invoiceNumber: string | null;
  date: string | null;
  description: string;
  amount: number;
  due: string | null;
  status: "Draft" | "Sent" | "Paid" | "Overdue";
  milestoneId?: number;
}

interface CostCategory {
  name: string;
  budget: number;
  actual: number;
  type?: string;
}

interface RoleBudget {
  monthlyHours?: Record<string, number>;
}

interface ProfitabilityBarLabelProps {
  x?: string | number;
  y?: string | number;
  width?: string | number;
  height?: string | number;
  value?: string | number;
}

/**
 * Value label for the Profitability Breakdown bars. Negative bars grow downwards
 * from the zero baseline, so their label sits below the bar instead of at the axis.
 */
function ProfitabilityBarLabel({ x, y, width, height, value }: ProfitabilityBarLabelProps) {
  if (value === undefined || value === null) return null;
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return null;

  const barX = Number(x);
  const barY = Number(y);
  const barWidth = Number(width);
  const barHeight = Number(height);
  if ([barX, barY, barWidth, barHeight].some(Number.isNaN)) return null;

  const isNegative = numericValue < 0;
  const labelY = isNegative ? barY + barHeight + 12 : barY - 5;

  return (
    <text
      x={barX + barWidth / 2}
      y={labelY}
      textAnchor="middle"
      fontSize={11}
      fill="var(--foreground)"
    >
      {formatCurrency(numericValue)}
    </text>
  );
}

export function FinancialsTab() {
  const ctx = useProjectDetailsContext();
  // Destructure all needed values from context
  const {
    project,
    navigate, setActiveTab,
    budgetData, enrichedBudgetData,
    timeEntries,
    fixedFeeInvoices, fixedFeeInvoicedToDate,
    setInvoiceCurrentPage,
    setInvoiceStatusFilter,
    effectiveCompletionPercent,
    evmExpanded, setEvmExpanded,
    financialAnalysisExpanded, setFinancialAnalysisExpanded,
    setTimeTabPrefilterBillable,
    // Additional items that may be used
  } = ctx;

  // ── Monthly summary data (BE-1) ────────────────────────────────────
  const [monthlySummary, setMonthlySummary] = useState<MonthlyFinancialSummary[]>([]);
  const [monthlySummaryLoading, setMonthlySummaryLoading] = useState(false);

  useEffect(() => {
    if (!project?.code) return;
    let cancelled = false;
    setMonthlySummaryLoading(true);
    fetchProjectMonthlySummary(project.code)
      .then((data) => { if (!cancelled) setMonthlySummary(data); })
      .catch(() => { /* silently fail — charts show empty state */ })
      .finally(() => { if (!cancelled) setMonthlySummaryLoading(false); });
    return () => { cancelled = true; };
  }, [project?.code]);

  // ── Cost rollup data (BE-4) ────────────────────────────────────────
  const [costRollup, setCostRollup] = useState<CategoryCostRollup[]>([]);

  useEffect(() => {
    if (!project?.id) return;
    let cancelled = false;
    fetchCostRollupByCategory(project.id)
      .then((data) => { if (!cancelled) setCostRollup(data); })
      .catch(() => { /* silently fail */ });
    return () => { cancelled = true; };
  }, [project?.id]);

  if (!project) return null;

  return (
    <>

                {/* ── Section 1: Performance Summary ── */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                    <h3 className="text-sm font-semibold text-foreground">Performance Summary</h3>
                    <div className="flex-1 h-px bg-border ml-1" />
                  </div>

                {/* Row 1 — Performance metric cards (3 cards) */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  {/* Card 1 — Sales Performance */}
                  {(() => {
                    const actualSales = enrichedBudgetData?.salesBudget?.totals?.actual ?? project.actualSales ?? project.salesActual ?? 0;
                    const budgetRevenue = enrichedBudgetData?.summary?.revenue?.budget ?? project.budgetRevenue ?? 0;
                    const salesVariance = actualSales - budgetRevenue;
                    const salesVariancePct = budgetRevenue !== 0 ? (salesVariance / budgetRevenue) * 100 : 0;
                    const remainingToDeliver = budgetRevenue - actualSales;
                    const isOnOrAboveBudget = actualSales >= budgetRevenue;
                    return (
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-sm font-medium">Sales Performance</CardTitle>
                        </CardHeader>
                        <CardContent>
                          {/* Hero metric */}
                          <div className="flex items-center gap-2">
                            <div className="text-2xl font-bold">
                              <SensitiveData>{formatCurrency(actualSales)}</SensitiveData>
                            </div>
                            {isOnOrAboveBudget ? (
                              <TrendingUp className="h-4 w-4 text-success-500" />
                            ) : (
                              <TrendingDown className="h-4 w-4 text-error-500" />
                            )}
                          </div>
                          <button
                            onClick={() => navigate(`/project-transactions?projectCode=${project.code}&entryType=S`)}
                            className="text-xs text-primary underline underline-offset-2 hover:opacity-70 transition-opacity mt-0.5 text-left"
                          >
                            Actual Sales
                          </button>

                          {/* Secondary rows */}
                          <div className="mt-3 space-y-1.5">
                            <div className="flex justify-between text-xs">
                              <button
                                onClick={() => setActiveTab('sales-budget')}
                                className="text-primary underline underline-offset-2 hover:opacity-70 transition-opacity"
                              >Budget Sales</button>
                              <SensitiveData>
                                <span className="font-medium">{formatCurrency(budgetRevenue)}</span>
                              </SensitiveData>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-muted-foreground">Variance (£)</span>
                              <SensitiveData>
                                <span className={`font-medium ${salesVariance >= 0 ? "text-success-600" : "text-error-600"}`}>
                                  {salesVariance >= 0 ? "+" : ""}{formatCurrency(salesVariance)}
                                </span>
                              </SensitiveData>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-muted-foreground">Variance (%)</span>
                              <SensitiveData>
                                <span className={`font-medium ${salesVariancePct >= 0 ? "text-success-600" : "text-error-600"}`}>
                                  {salesVariancePct >= 0 ? "+" : ""}{formatPercent(salesVariancePct)}
                                </span>
                              </SensitiveData>
                            </div>
                            <div className="flex justify-between text-xs border-t pt-1.5 mt-1">
                              <span className="text-muted-foreground">Remaining to Deliver</span>
                              <SensitiveData>
                                <span className={`font-medium ${remainingToDeliver <= 0 ? "text-success-600" : "text-muted-foreground"}`}>
                                  {formatCurrency(Math.max(remainingToDeliver, 0))}
                                </span>
                              </SensitiveData>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })()}

                  {/* Card 2 — Cost Performance */}
                  {(() => {
                    const costTotals = budgetData?.costBudget?.totals;
                    const actualCosts = costTotals?.actual ?? project.actualCosts ?? 0;
                    const budgetCosts = costTotals?.budget ?? ((project.budgetLabour ?? 0) + (project.budgetExpense ?? 0));
                    const costVariance = actualCosts - budgetCosts;
                    const costVariancePct = budgetCosts !== 0 ? (costVariance / budgetCosts) * 100 : 0;
                    const isOverBudget = actualCosts > budgetCosts;

                    // Contingency
                    const contingencyCat = budgetData?.costBudget?.categories?.find(
                      (c: { type?: string }) => c.type === 'contingency'
                    );
                    const contingencyBudget = (contingencyCat as { budget?: number } | undefined)?.budget ?? 0;
                    const contingencyActual = (contingencyCat as { actual?: number } | undefined)?.actual ?? 0;
                    const contingencyLeft = contingencyBudget - contingencyActual;

                    return (
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-sm font-medium">Cost Performance</CardTitle>
                        </CardHeader>
                        <CardContent>
                          {/* Hero metric */}
                          <div className="flex items-center gap-2">
                            <div className="text-2xl font-bold">
                              <SensitiveData>{formatCurrency(actualCosts)}</SensitiveData>
                            </div>
                            {isOverBudget ? (
                              <TrendingUp className="h-4 w-4 text-error-500" />
                            ) : (
                              <TrendingDown className="h-4 w-4 text-success-500" />
                            )}
                          </div>
                          <button
                            onClick={() => navigate(`/project-transactions?projectCode=${project.code}&entryType=C`)}
                            className="text-xs text-primary underline underline-offset-2 hover:opacity-70 transition-opacity mt-1"
                          >Actual Costs</button>

                          {/* Secondary rows */}
                          <div className="mt-3 space-y-1.5 text-xs">
                            <div className="flex justify-between">
                              <button
                                onClick={() => setActiveTab('cost-budget')}
                                className="text-primary underline underline-offset-2 hover:opacity-70 transition-opacity"
                              >Budget Costs</button>
                              <SensitiveData>
                                <span className="font-medium">{formatCurrency(budgetCosts)}</span>
                              </SensitiveData>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Variance (£)</span>
                              <SensitiveData>
                                <span className={`font-medium ${costVariance > 0 ? 'text-error-600' : costVariance < 0 ? 'text-success-600' : ''}`}>
                                  {costVariance >= 0 ? '+' : ''}{formatCurrency(costVariance)}
                                </span>
                              </SensitiveData>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Variance (%)</span>
                              <span className={`font-medium ${costVariancePct > 0 ? 'text-error-600' : costVariancePct < 0 ? 'text-success-600' : ''}`}>
                                {costVariancePct >= 0 ? '+' : ''}{formatPercent(costVariancePct)}
                              </span>
                            </div>
                            {contingencyBudget > 0 && (
                              <>
                                <div className="border-t pt-1.5 mt-1.5" />
                                <div className="flex justify-between items-center">
                                  <span className="text-muted-foreground">Contingency Left</span>
                                  <SensitiveData>
                                    <span className={`font-medium text-xs px-1.5 py-0.5 rounded-full ${
                                      contingencyLeft <= 0
                                        ? 'bg-error-100 text-error-700'
                                        : contingencyLeft < contingencyBudget * 0.25
                                        ? 'bg-warning-100 text-warning-700'
                                        : 'bg-success-100 text-success-700'
                                    }`}>
                                      {formatCurrency(contingencyLeft)}
                                    </span>
                                  </SensitiveData>
                                </div>
                              </>
                            )}
                          </div>

                          {/* Drill-through link — Task 28 */}
                          <div className="mt-3 pt-2 border-t">
                            <button
                              onClick={() => navigate(`/project-transactions?projectCode=${project.code}&entryType=C`)}
                              className="text-xs text-primary underline underline-offset-2 hover:opacity-70 transition-opacity"
                            >
                              View transactions →
                            </button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })()}

                  {/* Card 3 — Margin Performance */}
                  {(() => {
                    const actualCostsForMargin = budgetData?.costBudget?.totals?.actual ?? project.actualCosts ?? 0;
                    const budgetCostsForMargin = budgetData?.costBudget?.totals?.budget ?? ((project.budgetLabour ?? 0) + (project.budgetExpense ?? 0));

                    // Task 23 — use the company GP Calculation Method setting
                    const gpMethod = getFinanceSettings().gpCalculationMethod;

                    const actualResult = calculateGP(
                      {
                        revenue: enrichedBudgetData?.salesBudget?.totals?.actual ?? project.actualSales ?? project.salesActual ?? 0,
                        totalDirectCosts: actualCostsForMargin,
                        // Labour and subcontractor breakdowns are not available at the project
                        // summary level yet — contribution method will fall back to 0 for those
                        // until nominal code mapping (Task 24) provides the split.
                        labourCosts: project.actualLabourCosts ?? 0,
                        subcontractorCosts: project.actualSubcontractorCosts ?? 0,
                      },
                      gpMethod
                    );

                    const budgetResult = calculateBudgetGP(
                      {
                        revenue: project.budgetRevenue,
                        totalDirectCosts: budgetCostsForMargin,
                        labourCosts: project.budgetLabour ?? 0,
                        subcontractorCosts: 0, // subcontractor budget not available at summary level
                      },
                      gpMethod
                    );

                    const actualGP = actualResult.grossProfit;
                    const actualGMpct = actualResult.grossMarginPct;
                    const budgetGMpct = budgetResult.grossMarginPct;
                    const variancePp = actualGMpct - budgetGMpct;

                    // Health thresholds — driven by Settings (Task 22)
                    const marginThresholds = getBudgetHealthThresholds();
                    const amberThreshold = marginThresholds.marginAmberThreshold;
                    const redThreshold = marginThresholds.marginRedThreshold;

                    return (
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-sm font-medium">Margin Performance</CardTitle>
                        </CardHeader>
                        <CardContent>
                          {/* Hero metric */}
                          <div className="flex items-center gap-2">
                            <div className={`text-2xl font-bold ${actualGMpct >= amberThreshold ? 'text-success-600' : actualGMpct >= redThreshold ? 'text-warning-600' : 'text-error-600'}`}>
                              <SensitiveData>{formatPercent(actualGMpct)}</SensitiveData>
                            </div>
                            {/* Health badge */}
                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                              actualGMpct >= amberThreshold
                                ? 'bg-success-100 text-success-700'
                                : actualGMpct >= redThreshold
                                ? 'bg-warning-100 text-warning-700'
                                : 'bg-error-100 text-error-700'
                            }`}>
                              {actualGMpct >= amberThreshold ? 'Healthy' : actualGMpct >= redThreshold ? 'At Risk' : 'Critical'}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">Actual Gross Margin %</p>

                          {/* Secondary rows */}
                          <div className="mt-3 space-y-1.5 text-xs">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Budget GM%</span>
                              <span className="font-medium">
                                <SensitiveData>{formatPercent(budgetGMpct)}</SensitiveData>
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Variance</span>
                              <span className={`font-medium ${variancePp > 0 ? 'text-success-600' : variancePp < 0 ? 'text-error-600' : ''}`}>
                                {variancePp >= 0 ? '+' : ''}{variancePp.toFixed(1)}pp
                              </span>
                            </div>
                            <div className="border-t pt-1.5 mt-1.5" />
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Actual GP (£)</span>
                              <SensitiveData>
                                <span className={`font-medium ${actualGP >= 0 ? 'text-success-600' : 'text-error-600'}`}>
                                  {formatCurrency(actualGP)}
                                </span>
                              </SensitiveData>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })()}
                </div>
                </div>{/* end Performance Summary section */}

                {/* ── Section 2: Billing & Cash Flow ── */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-muted-foreground" />
                    <h3 className="text-sm font-semibold text-foreground">Billing &amp; Cash Flow</h3>
                    <div className="flex-1 h-px bg-border ml-1" />
                  </div>

                {/* Row 2 — Billing metric cards (3 cards) */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">

                  {/* Card 4 — WIP & Billing */}
                  {(() => {
                    const wipThresholds = getBudgetHealthThresholds();
                    const wip = project.wipAmount ?? 0;
                    const ready = project.unbilledAmount ?? 0;
                    const completionPct = project.completionPercent ?? 0;
                    const billingPct = project.budgetRevenue > 0
                      ? (fixedFeeInvoicedToDate / project.budgetRevenue) * 100
                      : 0;
                    const underBillingGap = completionPct - billingPct; // positive = under-billing

                    // WIP health — driven by Settings thresholds (not hardcoded)
                    const wipIsRed = wip >= wipThresholds.wipRedThreshold;
                    const wipIsAmber = !wipIsRed && wip >= wipThresholds.wipAmberThreshold;
                    const wipColor = wipIsRed
                      ? 'text-error-600'
                      : wipIsAmber
                      ? 'text-warning-600'
                      : 'text-foreground';

                    return (
                      <Card>
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-sm font-medium">WIP &amp; Billing</CardTitle>
                            {wipIsRed ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-error-50 px-2 py-0.5 text-xs font-medium text-error-700">
                                <AlertCircle className="h-3 w-3" /> High WIP
                              </span>
                            ) : wipIsAmber ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-warning-50 px-2 py-0.5 text-xs font-medium text-warning-700">
                                <AlertCircle className="h-3 w-3" /> Review
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 rounded-full bg-success-50 px-2 py-0.5 text-xs font-medium text-success-700">
                                <CheckCircle className="h-3 w-3" /> On Track
                              </span>
                            )}
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          {/* Hero — WIP */}
                          <div>
                            <div className={`text-2xl font-bold ${wipColor}`}>
                              <SensitiveData>{formatCurrency(wip)}</SensitiveData>
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">WIP (Unbilled)</p>
                          </div>

                          <Separator />

                          {/* Ready to Invoice */}
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">Ready to Invoice</span>
                            <span className="text-sm font-semibold text-success-600">
                              <SensitiveData>{formatCurrency(ready)}</SensitiveData>
                            </span>
                          </div>

                          {/* Under-billing gap */}
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">Billing vs Completion</span>
                            {underBillingGap > 0 ? (
                              <span className="text-xs font-medium text-error-600">
                                {underBillingGap.toFixed(1)}% behind billing
                              </span>
                            ) : underBillingGap < -2 ? (
                              <span className="text-xs font-medium text-success-600">
                                {Math.abs(underBillingGap).toFixed(1)}% ahead of completion
                              </span>
                            ) : (
                              <span className="text-xs font-medium text-success-600">In line</span>
                            )}
                          </div>

                          <Separator />

                          {/* WIP threshold context */}
                          <div className="text-xs text-muted-foreground">
                            Amber at {formatCurrency(wipThresholds.wipAmberThreshold)} · Red at {formatCurrency(wipThresholds.wipRedThreshold)}
                          </div>

                          <Separator />

                          {/* Drill-through → Billing tab */}
                          <button
                            onClick={() => setActiveTab('billing')}
                            className="text-xs text-primary underline underline-offset-2 hover:opacity-70 transition-opacity"
                          >
                            View in Billing
                          </button>
                        </CardContent>
                      </Card>
                    );
                  })()}

                  {/* Card 5 — Invoice Summary */}
                  {(() => {
                    const totalInvoiced = fixedFeeInvoicedToDate;

                    const draftInvoices = (fixedFeeInvoices as FixedFeeInvoice[]).filter((i: FixedFeeInvoice) => i.status === 'Draft');
                    const overdueInvoices = (fixedFeeInvoices as FixedFeeInvoice[]).filter((i: FixedFeeInvoice) => i.status === 'Overdue');
                    const draftValue = draftInvoices.reduce((s: number, i: FixedFeeInvoice) => s + i.amount, 0);
                    const overdueValue = overdueInvoices.reduce((s: number, i: FixedFeeInvoice) => s + i.amount, 0);

                    // Next invoice due — earliest upcoming unpaid invoice
                    const today = new Date();
                    const upcomingInvoices = (fixedFeeInvoices as FixedFeeInvoice[])
                      .filter((i: FixedFeeInvoice) => i.status !== 'Paid' && i.due)
                      .sort((a: FixedFeeInvoice, b: FixedFeeInvoice) => new Date(a.due!).getTime() - new Date(b.due!).getTime());
                    const nextDue = upcomingInvoices[0];

                    // Overdue aging buckets
                    const aging30 = overdueInvoices
                      .filter((i: FixedFeeInvoice) => {
                        const days = (today.getTime() - new Date(i.due!).getTime()) / 86400000;
                        return days <= 30;
                      })
                      .reduce((s: number, i: FixedFeeInvoice) => s + i.amount, 0);
                    const aging60 = overdueInvoices
                      .filter((i: FixedFeeInvoice) => {
                        const days = (today.getTime() - new Date(i.due!).getTime()) / 86400000;
                        return days > 30 && days <= 60;
                      })
                      .reduce((s: number, i: FixedFeeInvoice) => s + i.amount, 0);
                    const aging90 = overdueInvoices
                      .filter((i: FixedFeeInvoice) => {
                        const days = (today.getTime() - new Date(i.due!).getTime()) / 86400000;
                        return days > 60 && days <= 90;
                      })
                      .reduce((s: number, i: FixedFeeInvoice) => s + i.amount, 0);
                    const aging90plus = overdueInvoices
                      .filter((i: FixedFeeInvoice) => {
                        const days = (today.getTime() - new Date(i.due!).getTime()) / 86400000;
                        return days > 90;
                      })
                      .reduce((s: number, i: FixedFeeInvoice) => s + i.amount, 0);

                    const hasAgingIssues = aging30 > 0 || aging60 > 0 || aging90 > 0 || aging90plus > 0;

                    return (
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-medium">Invoice Summary</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          {/* Hero — Total Invoiced */}
                          <div>
                            <div className="text-2xl font-bold">
                              <SensitiveData>{formatCurrency(totalInvoiced)}</SensitiveData>
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">Total Invoiced</p>
                          </div>

                          <Separator />

                          {/* Draft invoices — clickable when there are drafts (Task 26) */}
                          <div className="flex items-center justify-between">
                            {draftInvoices.length > 0 ? (
                              <button
                                className="text-xs text-primary underline underline-offset-2 hover:text-primary/80 transition-colors text-left"
                                onClick={() => {
                                  setInvoiceStatusFilter('Draft');
                                  setInvoiceCurrentPage(1);
                                  setActiveTab('billing');
                                }}
                              >
                                Draft Invoices
                              </button>
                            ) : (
                              <span className="text-xs text-muted-foreground">Draft Invoices</span>
                            )}
                            <span className={`text-xs font-medium ${draftInvoices.length > 0 ? 'text-warning-600' : 'text-muted-foreground'}`}>
                              <SensitiveData>
                                {draftInvoices.length > 0 ? formatCurrency(draftValue) : '—'}
                              </SensitiveData>
                            </span>
                          </div>

                          {/* Overdue invoices — clickable when there are overdue (Task 26) */}
                          <div className="flex items-center justify-between">
                            {overdueInvoices.length > 0 ? (
                              <button
                                className="text-xs text-primary underline underline-offset-2 hover:text-primary/80 transition-colors text-left"
                                onClick={() => {
                                  setInvoiceStatusFilter('Overdue');
                                  setInvoiceCurrentPage(1);
                                  setActiveTab('billing');
                                }}
                              >
                                Overdue
                              </button>
                            ) : (
                              <span className="text-xs text-muted-foreground">Overdue</span>
                            )}
                            <span className={`text-xs font-medium ${overdueInvoices.length > 0 ? 'text-error-600' : 'text-muted-foreground'}`}>
                              <SensitiveData>
                                {overdueInvoices.length > 0 ? formatCurrency(overdueValue) : '—'}
                              </SensitiveData>
                            </span>
                          </div>

                          {/* Next invoice due */}
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">Next Due</span>
                            <span className="text-xs font-medium">
                              {nextDue?.due ? formatDate(nextDue.due) : '—'}
                            </span>
                          </div>

                          {/* Aging buckets — only shown if there are overdue invoices */}
                          {hasAgingIssues && (
                            <>
                              <Separator />
                              <div className="space-y-1">
                                <p className="text-xs text-muted-foreground uppercase tracking-wide">Overdue Aging</p>
                                {aging30 > 0 && (
                                  <button
                                    className="w-full flex items-center justify-between hover:opacity-70 transition-opacity"
                                    onClick={() => { setInvoiceStatusFilter('Overdue'); setInvoiceCurrentPage(1); setActiveTab('billing'); }}
                                  >
                                    <span className="text-xs text-primary underline underline-offset-2">0–30 days</span>
                                    <span className="text-xs font-medium text-warning-600">
                                      <SensitiveData>{formatCurrency(aging30)}</SensitiveData>
                                    </span>
                                  </button>
                                )}
                                {aging60 > 0 && (
                                  <button
                                    className="w-full flex items-center justify-between hover:opacity-70 transition-opacity"
                                    onClick={() => { setInvoiceStatusFilter('Overdue'); setInvoiceCurrentPage(1); setActiveTab('billing'); }}
                                  >
                                    <span className="text-xs text-primary underline underline-offset-2">31–60 days</span>
                                    <span className="text-xs font-medium text-warning-700">
                                      <SensitiveData>{formatCurrency(aging60)}</SensitiveData>
                                    </span>
                                  </button>
                                )}
                                {aging90 > 0 && (
                                  <button
                                    className="w-full flex items-center justify-between hover:opacity-70 transition-opacity"
                                    onClick={() => { setInvoiceStatusFilter('Overdue'); setInvoiceCurrentPage(1); setActiveTab('billing'); }}
                                  >
                                    <span className="text-xs text-primary underline underline-offset-2">61–90 days</span>
                                    <span className="text-xs font-medium text-error-600">
                                      <SensitiveData>{formatCurrency(aging90)}</SensitiveData>
                                    </span>
                                  </button>
                                )}
                                {aging90plus > 0 && (
                                  <button
                                    className="w-full flex items-center justify-between hover:opacity-70 transition-opacity"
                                    onClick={() => { setInvoiceStatusFilter('Overdue'); setInvoiceCurrentPage(1); setActiveTab('billing'); }}
                                  >
                                    <span className="text-xs text-primary underline underline-offset-2">90+ days</span>
                                    <span className="text-xs font-medium text-error-600">
                                      <SensitiveData>{formatCurrency(aging90plus)}</SensitiveData>
                                    </span>
                                  </button>
                                )}
                              </div>
                            </>
                          )}

                          {/* All clear state */}
                          {!hasAgingIssues && draftInvoices.length === 0 && overdueInvoices.length === 0 && (
                            <div className="flex items-center gap-1">
                              <CheckCircle className="h-3 w-3 text-success-500" />
                              <span className="text-xs text-success-600">No outstanding issues</span>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })()}

                  {/* Card 6 — Realisation Rate — Task 6 */}
                  {(() => {
                    // Potential revenue = SUM(billable approved hours × billRate per entry)
                    const billableEntries = (timeEntries as TimeEntry[]).filter((e: TimeEntry) => e.billable && e.approved);
                    const potentialRevenue = billableEntries.reduce(
                      (sum: number, e: TimeEntry) => sum + e.hours * e.billRate,
                      0
                    );
                    const actualBilled = enrichedBudgetData?.salesBudget?.totals?.actual ?? project.actualSales ?? project.salesActual ?? 0;
                    const hasRateData = billableEntries.length > 0 && potentialRevenue > 0;
                    const realisationRate = hasRateData
                      ? Math.round((actualBilled / potentialRevenue) * 100)
                      : null;
                    const leakage = hasRateData ? potentialRevenue - actualBilled : null;

                    // Health signal — thresholds driven by Settings (same as health rollup)
                    const _realisationThresholds = getBudgetHealthThresholds();
                    const rateHealth =
                      realisationRate === null
                        ? null
                        : realisationRate >= _realisationThresholds.realisationAmberThreshold
                        ? { label: "Healthy", colour: "text-success-600", bg: "bg-success-50 border-success-200" }
                        : realisationRate >= _realisationThresholds.realisationRedThreshold
                        ? { label: "At Risk", colour: "text-warning-600", bg: "bg-warning-50 border-warning-200" }
                        : { label: "Critical", colour: "text-error-600", bg: "bg-error-50 border-error-200" };

                    return (
                      <Card>
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-sm font-medium">Realisation Rate</CardTitle>
                            {rateHealth && (
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${rateHealth.bg} ${rateHealth.colour}`}>
                                {rateHealth.label}
                              </span>
                            )}
                          </div>
                        </CardHeader>
                        <CardContent>
                          {hasRateData ? (
                            <>
                              {/* Hero */}
                              <div className={`text-2xl font-bold ${rateHealth?.colour ?? ""}`}>
                                {realisationRate}%
                              </div>
                              <p className="text-xs text-muted-foreground mt-0.5">Realisation Rate</p>

                              {/* Secondary rows */}
                              <div className="mt-3 space-y-1.5">
                                <div className="flex items-center justify-between text-xs">
                                  <span className="text-muted-foreground">Actual Billed</span>
                                  <span className="font-medium">{formatCurrency(actualBilled)}</span>
                                </div>
                                <div className="flex items-center justify-between text-xs">
                                  <span className="text-muted-foreground">Potential Value</span>
                                  <span className="font-medium">{formatCurrency(potentialRevenue)}</span>
                                </div>
                                <div className="flex items-center justify-between text-xs">
                                  <span className="text-muted-foreground">Value Leakage</span>
                                  <span className={`font-medium ${(leakage ?? 0) > 0 ? "text-error-600" : "text-success-600"}`}>
                                    {leakage !== null ? formatCurrency(leakage) : "—"}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between text-xs">
                                  <span className="text-muted-foreground">Billable Entries</span>
                                  <span className="font-medium">{billableEntries.length}</span>
                                </div>
                              </div>
                              <button
                                className="mt-2 text-xs text-primary hover:underline text-left"
                                onClick={() => {
                                  setTimeTabPrefilterBillable(true);
                                  setActiveTab('time');
                                }}
                              >
                                View billable entries →
                              </button>
                            </>
                          ) : (
                            <>
                              <div className="text-2xl font-bold text-muted-foreground">—</div>
                              <p className="text-xs text-muted-foreground mt-1">Realisation Rate %</p>
                              <div className="flex items-center gap-1 mt-3">
                                <span className="text-xs text-muted-foreground italic">
                                  No approved billable timesheet entries found
                                </span>
                              </div>
                            </>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })()}
                </div>
                </div>{/* end Billing & Cash Flow section */}

                {/* ── Section 3: Financial Analysis (collapsible) ── */}
                <div className="border rounded-lg overflow-hidden">
                  {/* Header / toggle */}
                  <button
                    type="button"
                    onClick={() => setFinancialAnalysisExpanded((e: boolean) => !e)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-muted/40 hover:bg-muted/60 transition-colors text-left"
                  >
                    <div className="flex items-center gap-2">
                      <BarChart2 className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-semibold">Financial Analysis</span>
                      <span className="text-xs text-muted-foreground">(Charts &amp; Budget vs Actual)</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      {financialAnalysisExpanded ? 'Hide' : 'Show'}
                      {financialAnalysisExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </div>
                  </button>

                  {/* Expanded body */}
                  {financialAnalysisExpanded && (
                    <div className="px-4 pb-4 pt-2 space-y-4">

                {/* Row 3 — Financial performance charts */}
                <div className="grid gap-4 md:grid-cols-2">
                  {/* Profitability Grouped Bar Chart — Task 7 */}
                  {(() => {
                    const budgetSales  = project.budgetRevenue ?? 0;
                    const actualSales  = enrichedBudgetData?.salesBudget?.totals?.actual ?? project.actualSales ?? project.salesActual ?? 0;
                    const budgetCosts  = budgetData?.costBudget?.totals?.budget ?? (project.budgetLabour + project.budgetExpense);
                    const actualCosts  = budgetData?.costBudget?.totals?.actual ?? project.actualCosts ?? 0;
                    const _chartGpMethod = getFinanceSettings().gpCalculationMethod;
                    const budgetGP     = calculateBudgetGP(
                      { revenue: budgetSales, totalDirectCosts: budgetCosts, labourCosts: project.budgetLabour ?? 0, subcontractorCosts: 0 },
                      _chartGpMethod
                    ).grossProfit;
                    const actualGP     = calculateGP(
                      { revenue: actualSales, totalDirectCosts: actualCosts, labourCosts: project.actualLabourCosts ?? 0, subcontractorCosts: project.actualSubcontractorCosts ?? 0 },
                      _chartGpMethod
                    ).grossProfit;

                    // Gross profit is shown as-is: a loss must read as a negative bar rather
                    // than being floored at zero, which is indistinguishable from break-even.
                    const profitabilityChartData = [
                      { metric: "Sales",        Budget: budgetSales,  Actual: actualSales  },
                      { metric: "Costs",         Budget: budgetCosts,  Actual: actualCosts  },
                      { metric: "Gross Profit",  Budget: budgetGP,     Actual: actualGP     },
                    ];
                    const hasNegativeValue = profitabilityChartData.some(d => d.Budget < 0 || d.Actual < 0);

                    const profitabilityChartConfig: ChartConfig = {
                      Budget: { label: "Budget", color: "var(--chart-5)" },
                      Actual: { label: "Actual", color: "var(--chart-1)" },
                    };

                    return (
                      <Card>
                        <CardHeader>
                          <CardTitle>Profitability Breakdown</CardTitle>
                          <CardDescription>Budget vs Actual — Sales, Costs and Gross Profit</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <ChartContainer config={profitabilityChartConfig} className="h-[300px] w-full">
                            <RechartsBarChart
                              data={profitabilityChartData}
                              margin={{ top: 24, right: 20, left: 10, bottom: 5 }}
                              barCategoryGap="20%"
                              barGap={4}
                            >
                              <CartesianGrid strokeDasharray="3 3" vertical={false} />
                              <XAxis
                                dataKey="metric"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 12 }}
                              />
                              <YAxis
                                tickFormatter={(v) => `${v < 0 ? '-' : ''}£${Math.abs(v / 1000).toFixed(0)}k`}
                                domain={[(dataMin: number) => Math.min(0, dataMin), 'auto']}
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 11 }}
                                width={55}
                              />
                              <ChartTooltip
                                cursor={false}
                                content={
                                  <ChartTooltipContent
                                    formatter={(value, name) => (
                                      <span className="font-medium">{name}: {formatCurrency(value as number)}</span>
                                    )}
                                  />
                                }
                              />
                              <ChartLegend content={<ChartLegendContent />} />
                              {hasNegativeValue && (
                                <ReferenceLine y={0} stroke="var(--muted-foreground)" />
                              )}
                              <Bar dataKey="Budget" fill="var(--color-Budget)" radius={[3, 3, 0, 0]} maxBarSize={48}>
                                <LabelList dataKey="Budget" content={<ProfitabilityBarLabel />} />
                              </Bar>
                              <Bar dataKey="Actual"  fill="var(--color-Actual)"  radius={[3, 3, 0, 0]} maxBarSize={48}>
                                <LabelList dataKey="Actual" content={<ProfitabilityBarLabel />} />
                              </Bar>
                            </RechartsBarChart>
                          </ChartContainer>
                        </CardContent>
                      </Card>
                    );
                  })()}

                  {/* Margin Trend Chart — Task 8 (BE-1) */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Gross Margin Trend</CardTitle>
                      <CardDescription>
                        Gross margin % per period — project life ({project.startDate ? new Date(project.startDate).toLocaleDateString("en-GB", { month: "short", year: "numeric" }) : "start"} → {project.endDate ? new Date(Math.min(new Date(project.endDate).getTime(), Date.now())).toLocaleDateString("en-GB", { month: "short", year: "numeric" }) : "today"})
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {monthlySummary.length > 0 ? (() => {
                        const gmThresholds = getBudgetHealthThresholds();
                        const targetGmPct = gmThresholds?.marginAmberThreshold ?? 30;
                        const gmChartConfig: ChartConfig = {
                          grossMarginPercent: { label: "GM %", color: "var(--chart-1)" },
                          target: { label: "Target GM %", color: "var(--muted-foreground)" },
                        };
                        const gmChartId = "gm-trend-chart";
                        return (
                          <div data-chart={gmChartId} style={{ height: 300 }}>
                            <ChartStyle id={gmChartId} config={gmChartConfig} />
                            <ResponsiveContainer width="100%" height={300}>
                              <RechartsLineChart
                                data={monthlySummary.map(p => ({
                                  month: `${p.monthLabel} ${p.yearLabel.slice(-2)}`,
                                  grossMarginPercent: p.grossMarginPercent,
                                }))}
                                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                              >
                                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                                <XAxis dataKey="month" tick={{ fill: "var(--muted-foreground)", fontSize: 11 }} />
                                <YAxis
                                  tick={{ fill: "var(--muted-foreground)" }}
                                  tickFormatter={(v) => `${v}%`}
                                  domain={['auto', 'auto']}
                                />
                                <RechartsTooltip
                                  content={({ active, payload }) => {
                                    if (active && payload && payload.length) {
                                      const d = payload[0].payload;
                                      return (
                                        <div className="rounded-lg border bg-background p-2 shadow-sm text-xs">
                                          <p className="font-semibold mb-1">{d.month}</p>
                                          <p style={{ color: "var(--chart-1)" }}>GM: {d.grossMarginPercent.toFixed(1)}%</p>
                                        </div>
                                      );
                                    }
                                    return null;
                                  }}
                                />
                                <ReferenceLine
                                  y={targetGmPct}
                                  stroke="var(--muted-foreground)"
                                  strokeDasharray="4 4"
                                  label={{ value: `Target ${targetGmPct}%`, position: "insideTopRight", fill: "var(--muted-foreground)", fontSize: 11 }}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="grossMarginPercent"
                                  stroke="var(--color-grossMarginPercent)"
                                  strokeWidth={2}
                                  dot={{ fill: "var(--color-grossMarginPercent)", r: 3 }}
                                  name="GM %"
                                />
                              </RechartsLineChart>
                            </ResponsiveContainer>
                          </div>
                        );
                      })() : (
                        <div className="h-[300px] flex flex-col items-center justify-center gap-3 text-center">
                          <div className="rounded-full bg-muted p-4">
                            <TrendingUp className="h-8 w-8 text-muted-foreground" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{monthlySummaryLoading ? "Loading period data…" : "No period data yet"}</p>
                            <p className="text-xs text-muted-foreground mt-1 max-w-[260px]">
                              Monthly gross margin breakdown will appear here as the project progresses.
                            </p>
                          </div>
                          <div className="flex items-center gap-1.5 rounded-md border border-warning-200 bg-warning-50 px-3 py-1.5 text-xs text-warning-700">
                            <span className="font-medium">Overall GM%</span>
                            <span className="text-warning-500">·</span>
                            <span className="font-semibold">
                              {(enrichedBudgetData?.salesBudget?.totals?.actual ?? project.actualSales ?? project.salesActual ?? 0) > 0
                                ? `${((((enrichedBudgetData?.salesBudget?.totals?.actual ?? project.actualSales ?? project.salesActual ?? 0) - (budgetData?.costBudget?.totals?.actual ?? project.actualCosts ?? 0)) / (enrichedBudgetData?.salesBudget?.totals?.actual ?? project.actualSales ?? project.salesActual ?? 0)) * 100).toFixed(1)}%`
                                : "—"}
                            </span>
                            <span className="text-muted-foreground ml-1">(project to date)</span>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Row 4a — Cost Breakdown by Category (full-width pair) */}
                <div className="grid gap-4 md:grid-cols-2">
                  {/* Cost Breakdown by Category — Task 9 */}
                  <Card>
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle>Cost Breakdown by Category</CardTitle>
                          <CardDescription>Budget vs Actual cost mix — all cost types</CardDescription>
                        </div>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button className="text-muted-foreground hover:text-foreground transition-colors">
                                <Info className="h-4 w-4" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="left" className="max-w-[240px] text-xs">
                              Actual costs by category will improve in accuracy once cost category mapping is configured in Settings.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {(() => {
                        // Build category data from costBudget.categories — fallback to Labour + Expenses from project
                        const cats = budgetData?.costBudget?.categories ?? [];
                        const hasCostBudget = cats.length > 0;

                        // Chart palette — Evo Design System ramp tokens, spread around the colour wheel
                        // so adjacent stacked segments never look alike
                        const chartVars = [
                          "var(--teal-500)",
                          "var(--sapgreen-500)",
                          "var(--burntorange-500)",
                          "var(--brickred-500)",
                          "var(--purple-500)",
                          "var(--electricviolet-500)",
                          "var(--blueviolet-500)",
                          "var(--cyan-500)",
                          "var(--indigo-500)",
                        ];
                        const OTHER_CATEGORY = "Other";
                        const OTHER_COLOR = "var(--neutral-500)";
                        const MAX_NAMED_CATEGORIES = 8;
                        const colorFor = (name: string, i: number) =>
                          name === OTHER_CATEGORY ? OTHER_COLOR : chartVars[i % chartVars.length];

                        // Categories to render — either from cost budget or legacy two-slice fallback
                        const rawCategories = hasCostBudget
                          ? cats.filter((c: CostCategory) => c.budget > 0 || c.actual > 0)
                          : [
                              { name: "Labour", budget: project.budgetLabour ?? 0, actual: project.actualLabour ?? 0 },
                              { name: "Expenses", budget: project.budgetExpense ?? 0, actual: project.actualExpense ?? 0 },
                            ];

                        // Collapse to top 8 by spend + a single grey "Other" bucket
                        const categories: CostCategory[] =
                          rawCategories.length > MAX_NAMED_CATEGORIES + 1
                            ? (() => {
                                const sorted = [...rawCategories].sort(
                                  (a: CostCategory, b: CostCategory) =>
                                    (b.budget + b.actual) - (a.budget + a.actual)
                                );
                                const top = sorted.slice(0, MAX_NAMED_CATEGORIES);
                                const rest = sorted.slice(MAX_NAMED_CATEGORIES);
                                return [
                                  ...top,
                                  {
                                    name: OTHER_CATEGORY,
                                    budget: rest.reduce((s: number, c: CostCategory) => s + c.budget, 0),
                                    actual: rest.reduce((s: number, c: CostCategory) => s + c.actual, 0),
                                  } as CostCategory,
                                ];
                              })()
                            : rawCategories;

                        if (categories.length === 0) {
                          return (
                            <div className="h-[300px] flex flex-col items-center justify-center gap-2 text-center">
                              <div className="rounded-full bg-muted p-3">
                                <BarChart2 className="h-6 w-6 text-muted-foreground" />
                              </div>
                              <p className="text-sm text-muted-foreground">No cost category data available</p>
                            </div>
                          );
                        }

                        // Build ChartConfig dynamically
                        const chartConfig: ChartConfig = Object.fromEntries(
                          categories.map((cat: CostCategory, i: number) => [
                            cat.name,
                            { label: cat.name, color: colorFor(cat.name, i) },
                          ])
                        );

                        // Build cost rollup lookup by category name for actual overrides (BE-4)
                        const rollupByName = new Map(
                          costRollup.map(r => [r.categoryName, r])
                        );

                        // Two rows of data — Budget and Actual
                        const chartData = [
                          {
                            row: "Budget",
                            ...Object.fromEntries(categories.map((c: CostCategory) => [c.name, c.budget])),
                          },
                          {
                            row: "Actual",
                            ...Object.fromEntries(categories.map((c: CostCategory) => {
                              const rollup = rollupByName.get(c.name);
                              return [c.name, rollup ? rollup.actualAmount : c.actual];
                            })),
                          },
                        ];

                        const totalBudget = categories.reduce((s: number, c: CostCategory) => s + c.budget, 0);
                        const totalActual = categories.reduce((s: number, c: CostCategory) => {
                          const rollup = rollupByName.get(c.name);
                          return s + (rollup ? rollup.actualAmount : c.actual);
                        }, 0);

                        return (
                          <>
                            <ChartContainer config={chartConfig} className="h-[200px] w-full">
                              <RechartsBarChart
                                data={chartData}
                                layout="vertical"
                                margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                              >
                                <CartesianGrid horizontal={false} className="stroke-muted" />
                                <XAxis
                                  type="number"
                                  tickFormatter={(v) => `£${(v / 1000).toFixed(0)}k`}
                                  tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
                                  axisLine={false}
                                  tickLine={false}
                                />
                                <YAxis
                                  type="category"
                                  dataKey="row"
                                  tick={{ fill: "var(--muted-foreground)", fontSize: 12 }}
                                  axisLine={false}
                                  tickLine={false}
                                  width={48}
                                />
                                <ChartTooltip
                                  cursor={false}
                                  content={
                                    <ChartTooltipContent
                                      formatter={(value, name) =>
                                        [`${formatCurrency(value as number)}`, name as string]
                                      }
                                    />
                                  }
                                />
                                {categories.map((cat: CostCategory, i: number) => {
                                  const rollupEntry = rollupByName.get(cat.name);
                                  const nominals = rollupEntry?.nominalCodes ?? [];
                                  const qs = nominals.length > 0
                                    ? `/project-transactions?projectCode=${project.code}&entryType=C&analysisCodes=${encodeURIComponent(nominals.join(','))}`
                                    : `/project-transactions?projectCode=${project.code}&entryType=C`;
                                  return (
                                    <Bar
                                      key={cat.name}
                                      dataKey={cat.name}
                                      stackId="cost"
                                      fill={colorFor(cat.name, i)}
                                      radius={0}
                                      cursor="pointer"
                                      onClick={() => navigate(qs)}
                                    />
                                  );
                                })}
                              </RechartsBarChart>
                            </ChartContainer>

                            {/* Category legend with budget / actual amounts */}
                            <div className="mt-3 space-y-1.5">
                              {categories.map((cat: CostCategory, i: number) => {
                                const rollupEntry = rollupByName.get(cat.name);
                                const nominals = rollupEntry?.nominalCodes ?? [];
                                const drillQs = nominals.length > 0
                                  ? `/project-transactions?projectCode=${project.code}&entryType=C&analysisCodes=${encodeURIComponent(nominals.join(','))}`
                                  : `/project-transactions?projectCode=${project.code}&entryType=C`;
                                return (
                                <div
                                  key={cat.name}
                                  className="flex items-center justify-between text-xs cursor-pointer hover:bg-muted/50 rounded px-1 -mx-1 transition-colors"
                                  onClick={() => navigate(drillQs)}
                                >
                                  <div className="flex items-center gap-1.5">
                                    <div
                                      className="h-2.5 w-2.5 rounded-sm flex-shrink-0"
                                      style={{ backgroundColor: colorFor(cat.name, i) }}
                                    />
                                    <span className="text-muted-foreground">{cat.name}</span>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <span className="w-24 text-right tabular-nums text-muted-foreground">
                                      <SensitiveData>{formatCurrency(cat.budget)}</SensitiveData>
                                    </span>
                                    <span className={`w-24 text-right tabular-nums font-medium ${(rollupByName.get(cat.name)?.actualAmount ?? cat.actual) > cat.budget ? "text-destructive" : "text-foreground"}`}>
                                      <SensitiveData>{formatCurrency(rollupByName.get(cat.name)?.actualAmount ?? cat.actual)}</SensitiveData>
                                    </span>
                                  </div>
                                </div>
                                );
                              })}
                              {/* Totals row */}
                              <div className="flex items-center justify-between text-xs pt-1.5 border-t">
                                <span className="font-medium">Total</span>
                                <div className="flex items-center gap-3">
                                  <span className="w-24 text-right tabular-nums text-muted-foreground font-medium">
                                    <SensitiveData>{formatCurrency(totalBudget)}</SensitiveData>
                                  </span>
                                  <span className={`w-24 text-right tabular-nums font-semibold ${totalActual > totalBudget ? "text-destructive" : "text-foreground"}`}>
                                    <SensitiveData>{formatCurrency(totalActual)}</SensitiveData>
                                  </span>
                                </div>
                              </div>
                              {/* Budget / Actual column headers */}
                              <div className="flex items-center justify-end gap-3 text-xs text-muted-foreground pt-0.5">
                                <span className="w-24 text-right">Budget</span>
                                <span className="w-24 text-right">Actual</span>
                              </div>
                            </div>
                          </>
                        );
                      })()}
                    </CardContent>
                  </Card>

                </div>

                {/* Row 4b — Burn charts: Budget Burn-up + Hours Burndown */}
                <div className="grid gap-4 md:grid-cols-2">
                  {/* Budget Burn-up Chart — Task 10 */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Budget Burn-up</CardTitle>
                      <CardDescription>Cumulative spend vs planned S-curve and EAC forecast</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        {(() => {
                          // --- Data preparation ---
                          // Use enrichedBudgetData (memoized, includes local edits) with multiple fallbacks for budget
                          const costTotalsBudget = enrichedBudgetData?.costBudget?.totals?.budget;
                          const catSum = enrichedBudgetData?.costBudget?.categories?.reduce((s: number, c: CostCategory) => s + (c.budget ?? 0), 0) ?? 0;
                          const legacyBudget = (project.budgetLabour ?? 0) + (project.budgetExpense ?? 0);
                          const totalBudget = (costTotalsBudget && costTotalsBudget > 0)
                            ? costTotalsBudget
                            : catSum > 0
                              ? catSum
                              : legacyBudget;

                          // Guard: if we truly have no budget data yet, show empty state
                          if (!totalBudget || totalBudget <= 0) {
                            return (
                              <div className="h-[300px] flex flex-col items-center justify-center gap-2 text-center">
                                <div className="rounded-full bg-muted p-3">
                                  <TrendingUp className="h-6 w-6 text-muted-foreground" />
                                </div>
                                <p className="text-sm text-muted-foreground">No budget data available to plot burn-up</p>
                              </div>
                            );
                          }

                          const startDate = project.startDate ? new Date(project.startDate) : new Date();
                          const endDate = project.endDate ? new Date(project.endDate) : new Date(startDate.getTime() + 365 * 86400000);
                          const today = new Date();

                          // Total project duration in months (at least 1)
                          const totalMonths = Math.max(
                            1,
                            (endDate.getFullYear() - startDate.getFullYear()) * 12 +
                              (endDate.getMonth() - startDate.getMonth()),
                          );

                          // Minimum 60px per month so labels never crush on long projects
                          const chartWidth = Math.max(600, totalMonths * 60);

                          // EAC: use shared calculateEAC() — Simple method (BAC / CPI where CPI=1, i.e. actualCosts / completionPct)
                          const actualCosts = enrichedBudgetData?.costBudget?.totals?.actual ?? project.actualCosts ?? 0;
                          const eacResult = effectiveCompletionPercent > 0
                            ? calculateEAC(
                                {
                                  actualCosts,
                                  totalBudget,
                                  completionPercent: effectiveCompletionPercent,
                                },
                                'simple',
                              )
                            : null;
                          const eacTotal = eacResult?.eac ?? null;

                          // Find the "today" period index (0-based month offset from start)
                          const todayMonthOffset = Math.min(
                            totalMonths,
                            Math.max(
                              0,
                              (today.getFullYear() - startDate.getFullYear()) * 12 +
                                (today.getMonth() - startDate.getMonth()),
                            ),
                          );

                          const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

                          // Build a lookup of monthly summary by "Mon YY" label for the actual spend line
                          const monthlyCumulativeCostMap = new Map<string, number>();
                          for (const ms of monthlySummary) {
                            const key = `${ms.monthLabel} ${ms.yearLabel.slice(-2)}`;
                            monthlyCumulativeCostMap.set(key, ms.cumulativeCost);
                          }

                          const dataPoints: { month: string; planned: number; eac: number | null; actual: number | null; budget: number; isToday: boolean }[] = [];

                          for (let i = 0; i <= totalMonths; i++) {
                            const date = new Date(startDate);
                            date.setMonth(date.getMonth() + i);

                            // S-curve: budget × (3t² - 2t³) where t = i / totalMonths
                            const t = i / totalMonths;
                            const planned = totalBudget * (3 * t * t - 2 * t * t * t);

                            // EAC line: only show from today onwards as a dashed projection
                            let eacValue: number | null = null;
                            if (eacTotal !== null) {
                              const eacT = i / totalMonths;
                              eacValue = eacTotal * (3 * eacT * eacT - 2 * eacT * eacT * eacT);
                            }

                            const monthLabel = `${monthNames[date.getMonth()]} ${date.getFullYear().toString().slice(2)}`;
                            const actualCumCost = monthlyCumulativeCostMap.get(monthLabel) ?? null;

                            dataPoints.push({
                              month: monthLabel,
                              planned: Math.round(planned),
                              eac: eacValue !== null ? Math.round(eacValue) : null,
                              actual: actualCumCost !== null ? Math.round(actualCumCost) : null,
                              budget: totalBudget,
                              isToday: i === todayMonthOffset,
                            });
                          }

                          // Find the label of the "today" data point for the vertical reference line
                          const todayLabel = dataPoints[todayMonthOffset]?.month ?? "";

                          const burnUpChartConfig: ChartConfig = {
                            planned: { label: "Planned Spend",  color: "var(--chart-1)" },
                            actual:  { label: "Actual Spend",   color: "var(--chart-4)" },
                            eac:     { label: "EAC Forecast",   color: "var(--chart-3)" },
                            today:   { label: "Today",          color: "var(--chart-2)" },
                          };

                          const burnUpChartId = "burn-up-chart";

                          return (
                            <div data-chart={burnUpChartId} style={{ minWidth: chartWidth, height: 300 }}>
                              <ChartStyle id={burnUpChartId} config={burnUpChartConfig} />
                              <ResponsiveContainer width="100%" height={300}>
                              <RechartsLineChart
                                data={dataPoints}
                                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                              >
                                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                                <XAxis
                                  dataKey="month"
                                  className="text-xs"
                                  tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
                                  interval={0}
                                />
                                <YAxis
                                  className="text-xs"
                                  tick={{ fill: "var(--muted-foreground)" }}
                                  tickFormatter={(value) => `£${(value / 1000).toFixed(0)}k`}
                                />
                                <RechartsTooltip
                                  content={({ active, payload }) => {
                                    if (active && payload && payload.length) {
                                      const d = payload[0].payload as BurnPoint & { actual?: number | null };
                                      return (
                                        <div className="rounded-lg border bg-background p-2 shadow-sm text-xs">
                                          <p className="font-semibold mb-1">{d.month}{d.isToday ? " (Today)" : ""}</p>
                                          <p style={{ color: "var(--chart-1)" }}>
                                            Planned: {formatCurrency(d.planned)}
                                          </p>
                                          {d.actual != null && (
                                            <p style={{ color: "var(--chart-4)" }}>
                                              Actual: {formatCurrency(d.actual)}
                                            </p>
                                          )}
                                          {d.eac !== null && (
                                            <p style={{ color: "var(--chart-3)" }}>
                                              EAC Forecast: {formatCurrency(d.eac)}
                                            </p>
                                          )}
                                          <p className="text-muted-foreground">
                                            Budget Ceiling: {formatCurrency(d.budget)}
                                          </p>
                                        </div>
                                      );
                                    }
                                    return null;
                                  }}
                                />
                                {/* Budget ceiling horizontal reference line */}
                                <ReferenceLine
                                  y={totalBudget}
                                  stroke="var(--muted-foreground)"
                                  strokeDasharray="4 4"
                                  label={{
                                    value: "Budget",
                                    position: "insideTopRight",
                                    fill: "var(--muted-foreground)",
                                    fontSize: 11,
                                  }}
                                />
                                {/* Today vertical marker */}
                                <ReferenceLine
                                  x={todayLabel}
                                  stroke="var(--color-today)"
                                  strokeDasharray="4 4"
                                  strokeWidth={1.5}
                                  label={{
                                    value: "Today",
                                    position: "insideTopLeft",
                                    fill: "var(--chart-2)",
                                    fontSize: 11,
                                  }}
                                />
                                {/* Planned S-curve */}
                                <Line
                                  type="monotone"
                                  dataKey="planned"
                                  stroke="var(--color-planned)"
                                  strokeWidth={2}
                                  dot={false}
                                  name="Planned Spend"
                                />
                                {/* Actual spend line (BE-1) */}
                                <Line
                                  type="monotone"
                                  dataKey="actual"
                                  stroke="var(--color-actual)"
                                  strokeWidth={2.5}
                                  dot={{ fill: "var(--color-actual)", r: 3 }}
                                  name="Actual Spend"
                                  connectNulls={true}
                                />
                                {/* EAC forecast — dashed */}
                                <Line
                                  type="monotone"
                                  dataKey="eac"
                                  stroke="var(--color-eac)"
                                  strokeWidth={2}
                                  strokeDasharray="6 3"
                                  dot={false}
                                  name="EAC Forecast"
                                  connectNulls={false}
                                />
                              </RechartsLineChart>
                              </ResponsiveContainer>
                            </div>
                          );
                        })()}
                      </div>

                    </CardContent>
                  </Card>

                  {/* Hours Burndown Chart — Task 11 */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Hours Burndown</CardTitle>
                      <CardDescription>Remaining hours vs plan — tracking to budget</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        {(() => {
                          const budgetHours = project.budgetHours ?? 0;
                          const actualHours = project.actualHours ?? 0;

                          if (!budgetHours || budgetHours <= 0) {
                            return (
                              <div className="h-[300px] flex flex-col items-center justify-center gap-2 text-center">
                                <div className="rounded-full bg-muted p-3">
                                  <TrendingUp className="h-6 w-6 text-muted-foreground" />
                                </div>
                                <p className="text-sm text-muted-foreground">No hours budget set for this project</p>
                              </div>
                            );
                          }

                          const startDate = project.startDate ? new Date(project.startDate) : new Date();
                          const endDate = project.endDate
                            ? new Date(project.endDate)
                            : new Date(startDate.getTime() + 365 * 86400000);
                          const today = new Date();

                          const totalMonths = Math.max(
                            1,
                            (endDate.getFullYear() - startDate.getFullYear()) * 12 +
                              (endDate.getMonth() - startDate.getMonth()),
                          );

                          // Minimum 60px per month so labels never crush on long projects
                          const chartWidth = Math.max(600, totalMonths * 60);

                          // Build cumulative planned hours per period from monthlyAllocations in
                          // the resource budget. Fall back to a linear ramp if not available.
                          const roleBudgets = enrichedBudgetData?.resourceBudget?.byRole ?? [];
                          const hasMonthlyData = roleBudgets.some(
                            (r: RoleBudget) => r.monthlyHours && Object.keys(r.monthlyHours).length > 0,
                          );

                          // Aggregate monthly planned hours across all roles keyed by YYYY-MM
                          let monthlyPlanned: number[] = Array(totalMonths + 1).fill(0);
                          if (hasMonthlyData) {
                            roleBudgets.forEach((role: RoleBudget) => {
                              if (role.monthlyHours) {
                                const sortedKeys = Object.keys(role.monthlyHours).sort();
                                sortedKeys.forEach((key, idx) => {
                                  if (idx <= totalMonths) {
                                    monthlyPlanned[idx] = (monthlyPlanned[idx] ?? 0) + (role.monthlyHours![key] ?? 0);
                                  }
                                });
                              }
                            });
                          } else {
                            // Linear distribution fallback
                            const hoursPerMonth = budgetHours / totalMonths;
                            monthlyPlanned = Array(totalMonths + 1)
                              .fill(0)
                              .map((_, i) => (i === 0 ? 0 : hoursPerMonth));
                          }

                          // EAC: forecastAtCompletionHours — use burn-rate projection from available data
                          // actualHours / completionPct gives EAC; fall back to null if no completion data
                          const completionPct =
                            effectiveCompletionPercent > 0 ? effectiveCompletionPercent / 100 : null;
                          const eacTotalHours =
                            completionPct && completionPct > 0 && actualHours > 0
                              ? actualHours / completionPct
                              : null;

                          // Today month offset
                          const todayMonthOffset = Math.min(
                            totalMonths,
                            Math.max(
                              0,
                              (today.getFullYear() - startDate.getFullYear()) * 12 +
                                (today.getMonth() - startDate.getMonth()),
                            ),
                          );

                          const monthNames = [
                            "Jan","Feb","Mar","Apr","May","Jun",
                            "Jul","Aug","Sep","Oct","Nov","Dec",
                          ];

                          // Build a lookup of cumulative hours by "Mon YY" label from monthly summary
                          const monthlyCumulativeHoursMap = new Map<string, number>();
                          for (const ms of monthlySummary) {
                            const key = `${ms.monthLabel} ${ms.yearLabel.slice(-2)}`;
                            monthlyCumulativeHoursMap.set(key, ms.cumulativeHours);
                          }

                          // Build data points — remaining hours counts DOWN from budgetHours to 0
                          let cumulativePlanned = 0;
                          const dataPoints: (HoursBurndownPoint & { actualRemaining?: number | null })[] = [];

                          for (let i = 0; i <= totalMonths; i++) {
                            const date = new Date(startDate);
                            date.setMonth(date.getMonth() + i);

                            cumulativePlanned += monthlyPlanned[i] ?? 0;
                            const plannedRemaining = Math.max(0, budgetHours - cumulativePlanned);

                            // EAC remaining: project from EAC total using same S-curve shape as planned
                            let eacRemaining: number | null = null;
                            if (eacTotalHours !== null) {
                              const t = totalMonths > 0 ? i / totalMonths : 1;
                              const eacConsumed = eacTotalHours * (3 * t * t - 2 * t * t * t);
                              eacRemaining = Math.max(0, eacTotalHours - eacConsumed);
                            }

                            const monthLabel = `${monthNames[date.getMonth()]} ${date.getFullYear().toString().slice(2)}`;
                            const cumActualHours = monthlyCumulativeHoursMap.get(monthLabel);
                            const actualRemainingVal = cumActualHours != null
                              ? Math.max(0, budgetHours - cumActualHours)
                              : null;

                            dataPoints.push({
                              month: monthLabel,
                              planned: Math.round(plannedRemaining),
                              eac: eacRemaining !== null ? Math.round(eacRemaining) : null,
                              actualRemaining: actualRemainingVal !== null ? Math.round(actualRemainingVal) : null,
                              isToday: i === todayMonthOffset,
                            });
                          }

                          const todayLabel = dataPoints[todayMonthOffset]?.month ?? "";

                          const burnDownChartConfig: ChartConfig = {
                            planned:         { label: "Planned Remaining", color: "var(--chart-1)" },
                            actualRemaining: { label: "Actual Remaining",  color: "var(--chart-4)" },
                            eac:             { label: "EAC Forecast",      color: "var(--chart-3)" },
                            today:           { label: "Today",             color: "var(--chart-2)" },
                          };

                          const burnDownChartId = "burn-down-chart";

                          return (
                            <div data-chart={burnDownChartId} style={{ minWidth: chartWidth, height: 300 }}>
                              <ChartStyle id={burnDownChartId} config={burnDownChartConfig} />
                              <ResponsiveContainer width="100%" height={300}>
                              <RechartsLineChart
                                data={dataPoints}
                                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                              >
                                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                                <XAxis
                                  dataKey="month"
                                  tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
                                  interval={0}
                                />
                                <YAxis
                                  tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
                                  tickFormatter={(v) => `${v}h`}
                                />
                                <RechartsTooltip
                                  content={({ active, payload }) => {
                                    if (active && payload && payload.length) {
                                      const d = payload[0].payload as HoursBurndownPoint & { actualRemaining?: number | null };
                                      return (
                                        <div className="rounded-lg border bg-background p-2 shadow-sm text-xs">
                                          <p className="font-semibold mb-1">
                                            {d.month}
                                            {d.isToday ? " (Today)" : ""}
                                          </p>
                                          <p style={{ color: "var(--chart-1)" }}>
                                            Planned remaining: {d.planned}h
                                          </p>
                                          {d.actualRemaining != null && (
                                            <p style={{ color: "var(--chart-4)" }}>
                                              Actual remaining: {d.actualRemaining}h
                                            </p>
                                          )}
                                          {d.eac !== null && (
                                            <p style={{ color: "var(--chart-3)" }}>
                                              EAC remaining: {d.eac}h
                                            </p>
                                          )}
                                        </div>
                                      );
                                    }
                                    return null;
                                  }}
                                />
                                {/* Zero reference line — budget exhausted */}
                                <ReferenceLine
                                  y={0}
                                  stroke="var(--muted-foreground)"
                                  strokeDasharray="4 4"
                                  label={{
                                    value: "Budget exhausted",
                                    position: "insideBottomRight",
                                    fill: "var(--muted-foreground)",
                                    fontSize: 10,
                                  }}
                                />
                                {/* Today vertical marker */}
                                <ReferenceLine
                                  x={todayLabel}
                                  stroke="var(--color-today)"
                                  strokeDasharray="4 4"
                                  strokeWidth={1.5}
                                  label={{
                                    value: "Today",
                                    position: "insideTopLeft",
                                    fill: "var(--chart-2)",
                                    fontSize: 11,
                                  }}
                                />
                                {/* Planned remaining hours — solid */}
                                <Line
                                  type="monotone"
                                  dataKey="planned"
                                  stroke="var(--color-planned)"
                                  strokeWidth={2}
                                  dot={false}
                                  name="Planned Remaining"
                                />
                                {/* Actual remaining hours (BE-1) */}
                                <Line
                                  type="monotone"
                                  dataKey="actualRemaining"
                                  stroke="var(--color-actualRemaining)"
                                  strokeWidth={2.5}
                                  dot={{ fill: "var(--color-actualRemaining)", r: 3 }}
                                  name="Actual Remaining"
                                  connectNulls={true}
                                />
                                {/* EAC forecast — dashed */}
                                <Line
                                  type="monotone"
                                  dataKey="eac"
                                  stroke="var(--color-eac)"
                                  strokeWidth={2}
                                  strokeDasharray="6 3"
                                  dot={false}
                                  name="EAC Forecast"
                                  connectNulls={false}
                                />
                              </RechartsLineChart>
                              </ResponsiveContainer>
                            </div>
                          );
                        })()}
                      </div>

                    </CardContent>
                  </Card>
                </div>

                {/* Row 5 — Full width Budget vs Actual Table */}
                <div className="grid gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Budget vs Actual</CardTitle>
                      <CardDescription>Detailed variance analysis</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        {/* EAC helper — derived once, used across all rows */}
                        {(() => {
                          const completionPct = effectiveCompletionPercent > 0 ? effectiveCompletionPercent / 100 : 0;

                          // EAC: Revenue
                          const salesActual = enrichedBudgetData?.salesBudget?.totals?.actual ?? project.actualSales ?? project.salesActual ?? 0;
                          const eacRevenue = project.type === 'fixed-price'
                            ? project.budgetRevenue
                            : (completionPct > 0 ? (salesActual / completionPct) : project.budgetRevenue);

                          // EAC: Total Costs
                          const costBudgetTotalsActual = enrichedBudgetData?.costBudget?.totals?.actual ?? project.actualCosts ?? 0;
                          const costBudgetTotalsBudget = enrichedBudgetData?.costBudget?.totals?.budget ?? 0;
                          const eacTotalCosts = completionPct > 0
                            ? costBudgetTotalsActual / completionPct
                            : costBudgetTotalsBudget;

                          // EAC: Gross Profit = EAC Revenue − EAC Total Costs
                          const eacGrossProfit = eacRevenue - eacTotalCosts;

                          // EAC: Gross Margin % = (EAC GP / EAC Revenue) × 100
                          const eacGrossMarginPct = eacRevenue !== 0 ? (eacGrossProfit / eacRevenue) * 100 : 0;

                          // EAC: Hours — burn-rate projection
                          const actualHours = project.actualHours ?? 0;
                          const eacHours = completionPct > 0 && actualHours > 0
                            ? actualHours / completionPct
                            : project.budgetHours;

                          return (
                            <>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Category</TableHead>
                                  <TableHead className="text-right">
                                    <span className="inline-flex items-center gap-1.5">
                                      Budget
                                      {(project.budgetRevisionCount ?? 0) > 0 && (
                                          <TooltipProvider>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <span className="inline-flex items-center gap-0.5 rounded-sm bg-warning-100 px-1 py-0.5 text-xs font-semibold text-warning-700 cursor-help">
                                                  Revised ↑
                                                </span>
                                              </TooltipTrigger>
                                              <TooltipContent side="top" className="text-xs max-w-[260px]">
                                                Original: {formatCurrency(project.originalBudgetRevenue ?? 0)} revenue
                                                / {formatCurrency(project.originalBudgetCosts ?? 0)} costs
                                                → Current: {formatCurrency(project.budgetRevenue)} revenue
                                                / {formatCurrency((project.budgetLabour ?? 0) + (project.budgetExpense ?? 0))} costs
                                                {' '}(revised {project.budgetRevisionCount} time{project.budgetRevisionCount === 1 ? '' : 's'})
                                              </TooltipContent>
                                            </Tooltip>
                                          </TooltipProvider>
                                        )}
                                    </span>
                                  </TableHead>
                                  <TableHead className="text-right">Actual</TableHead>
                                  <TableHead className="text-right">Variance (£)</TableHead>
                                  <TableHead className="text-right">Variance %</TableHead>
                                  <TableHead className="text-right">
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <span className="cursor-help underline decoration-dotted underline-offset-2">EAC</span>
                                        </TooltipTrigger>
                                        <TooltipContent side="top" className="text-xs">
                                          Forecast at Completion
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  </TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {/* Revenue */}
                                <TableRow>
                                  <TableCell>
                                    <button
                                      onClick={() => navigate(`/project-transactions?projectCode=${project.code}&entryType=S`)}
                                      className="text-primary underline underline-offset-2 hover:opacity-70 transition-opacity"
                                    >
                                      Sales
                                    </button>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <SensitiveData>{formatCurrency(project.budgetRevenue)}</SensitiveData>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <SensitiveData>{formatCurrency(project.actualRevenue)}</SensitiveData>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {(() => {
                                      const pct = project.budgetRevenue !== 0
                                        ? ((project.actualRevenue - project.budgetRevenue) / project.budgetRevenue) * 100
                                        : 0;
                                      return (
                                        // Revenue: positive variance (over-billing) is good → invert sign for colour logic
                                        <span className={getVarianceColor(-pct)}>
                                          <SensitiveData>
                                            {formatCurrency(project.actualRevenue - project.budgetRevenue)}
                                          </SensitiveData>
                                        </span>
                                      );
                                    })()}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {(() => {
                                      const pct = project.budgetRevenue !== 0
                                        ? ((project.actualRevenue - project.budgetRevenue) / project.budgetRevenue) * 100
                                        : 0;
                                      return (
                                        // Revenue: positive variance (over-billing) is good → invert sign for colour logic
                                        <span className={getVarianceColor(-pct)}>
                                          {pct > 0 ? '+' : ''}{pct.toFixed(1)}%
                                        </span>
                                      );
                                    })()}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <SensitiveData>{formatCurrency(eacRevenue)}</SensitiveData>
                                  </TableCell>
                                </TableRow>

                                {/* Total Costs */}
                                <TableRow>
                                  <TableCell>
                                    <button
                                      onClick={() => navigate(`/project-transactions?projectCode=${project.code}&entryType=C`)}
                                      className="text-primary underline underline-offset-2 hover:opacity-70 transition-opacity"
                                    >
                                      Total Costs
                                    </button>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <SensitiveData>
                                      {formatCurrency(costBudgetTotalsBudget)}
                                    </SensitiveData>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <SensitiveData>{formatCurrency(project.actualCosts)}</SensitiveData>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {(() => {
                                      const pct = costBudgetTotalsBudget !== 0
                                        ? ((project.actualCosts - costBudgetTotalsBudget) / costBudgetTotalsBudget) * 100
                                        : 0;
                                      return (
                                        <span className={getVarianceColor(pct)}>
                                          <SensitiveData>
                                            {formatCurrency(project.actualCosts - costBudgetTotalsBudget)}
                                          </SensitiveData>
                                        </span>
                                      );
                                    })()}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {(() => {
                                      const pct = costBudgetTotalsBudget !== 0
                                        ? ((project.actualCosts - costBudgetTotalsBudget) / costBudgetTotalsBudget) * 100
                                        : 0;
                                      return (
                                        <span className={getVarianceColor(pct)}>
                                          {pct > 0 ? '+' : ''}{pct.toFixed(1)}%
                                        </span>
                                      );
                                    })()}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <SensitiveData>{formatCurrency(eacTotalCosts)}</SensitiveData>
                                  </TableCell>
                                </TableRow>

                                {/* Gross Profit — subtotal row, visually separated */}
                                <TableRow className="border-t bg-muted/40">
                                  <TableCell className="font-semibold">Gross Profit</TableCell>
                                  <TableCell className="text-right font-semibold">
                                    <SensitiveData>
                                      {formatCurrency(project.budgetRevenue - costBudgetTotalsBudget)}
                                    </SensitiveData>
                                  </TableCell>
                                  <TableCell className="text-right font-semibold">
                                    <SensitiveData>
                                      {formatCurrency(salesActual - costBudgetTotalsActual)}
                                    </SensitiveData>
                                  </TableCell>
                                  <TableCell className="text-right font-semibold">
                                    {(() => {
                                      const budgetGP = project.budgetRevenue - costBudgetTotalsBudget;
                                      const actualGP = salesActual - costBudgetTotalsActual;
                                      const pct = budgetGP !== 0 ? ((actualGP - budgetGP) / budgetGP) * 100 : 0;
                                      return (
                                        // Gross Profit: positive variance (beating plan) is good → invert sign for colour logic
                                        <span className={getVarianceColor(-pct)}>
                                          <SensitiveData>
                                            {formatCurrency(actualGP - budgetGP)}
                                          </SensitiveData>
                                        </span>
                                      );
                                    })()}
                                  </TableCell>
                                  <TableCell className="text-right font-semibold">
                                    {(() => {
                                      const budgetGP = project.budgetRevenue - costBudgetTotalsBudget;
                                      const actualGP = salesActual - costBudgetTotalsActual;
                                      const pct = budgetGP !== 0 ? ((actualGP - budgetGP) / budgetGP) * 100 : 0;
                                      return (
                                        // Gross Profit: positive variance (beating plan) is good → invert sign for colour logic
                                        <span className={getVarianceColor(-pct)}>
                                          {pct > 0 ? '+' : ''}{pct.toFixed(1)}%
                                        </span>
                                      );
                                    })()}
                                  </TableCell>
                                  <TableCell className="text-right font-semibold">
                                    <SensitiveData>{formatCurrency(eacGrossProfit)}</SensitiveData>
                                  </TableCell>
                                </TableRow>

                                {/* Gross Margin % — sub-row beneath Gross Profit */}
                                <TableRow className="bg-muted/40">
                                  <TableCell className="font-semibold pl-6 text-muted-foreground">Gross Margin %</TableCell>
                                  <TableCell className="text-right font-semibold text-muted-foreground">
                                    {(() => {
                                      const budgetGP = project.budgetRevenue - costBudgetTotalsBudget;
                                      const budgetGMPct = project.budgetRevenue !== 0
                                        ? (budgetGP / project.budgetRevenue) * 100
                                        : 0;
                                      return `${budgetGMPct.toFixed(1)}%`;
                                    })()}
                                  </TableCell>
                                  <TableCell className="text-right font-semibold text-muted-foreground">
                                    {(() => {
                                      const actualGP = salesActual - costBudgetTotalsActual;
                                      const actualGMPct = salesActual !== 0
                                        ? (actualGP / salesActual) * 100
                                        : 0;
                                      return `${actualGMPct.toFixed(1)}%`;
                                    })()}
                                  </TableCell>
                                  <TableCell className="text-right font-semibold">
                                    {(() => {
                                      const budgetGP = project.budgetRevenue - costBudgetTotalsBudget;
                                      const actualGP = salesActual - costBudgetTotalsActual;
                                      const budgetGMPct = project.budgetRevenue !== 0
                                        ? (budgetGP / project.budgetRevenue) * 100
                                        : 0;
                                      const actualGMPct = salesActual !== 0
                                        ? (actualGP / salesActual) * 100
                                        : 0;
                                      const ppVariance = actualGMPct - budgetGMPct;
                                      return (
                                        // Higher margin than budget is good → invert sign for colour logic
                                        <span className={getVarianceColor(-ppVariance)}>
                                          {ppVariance >= 0 ? '+' : ''}{ppVariance.toFixed(1)}pp
                                        </span>
                                      );
                                    })()}
                                  </TableCell>
                                  {/* Variance % — not applicable for a % row */}
                                  <TableCell className="text-right text-muted-foreground">—</TableCell>
                                  <TableCell className="text-right font-semibold text-muted-foreground">
                                    {eacRevenue !== 0
                                      ? `${eacGrossMarginPct.toFixed(1)}%`
                                      : '—'}
                                  </TableCell>
                                </TableRow>

                                {/* Hours — separate section below profitability */}
                                <TableRow className="border-t">
                                  <TableCell>Hours</TableCell>
                                  <TableCell className="text-right">
                                    {project.budgetHours != null ? `${project.budgetHours.toLocaleString()}h` : '—'}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {project.actualHours != null ? `${(project.actualHours ?? 0).toLocaleString()}h` : '—'}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {(() => {
                                      const budgetH = project.budgetHours ?? 0;
                                      const actualH = project.actualHours ?? 0;
                                      const variance = actualH - budgetH;
                                      return (
                                        // Over-hours is bad → positive variance = red (same as cost rows)
                                        <span className={getVarianceColor(budgetH !== 0 ? (variance / budgetH) * 100 : 0)}>
                                          {variance >= 0 ? '+' : ''}{variance.toLocaleString()}h
                                        </span>
                                      );
                                    })()}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {(() => {
                                      const budgetH = project.budgetHours ?? 0;
                                      const actualH = project.actualHours ?? 0;
                                      const variance = actualH - budgetH;
                                      const pct = budgetH !== 0 ? (variance / budgetH) * 100 : 0;
                                      return (
                                        // Over-hours is bad → positive variance = red
                                        <span className={getVarianceColor(pct)}>
                                          {pct > 0 ? '+' : ''}{pct.toFixed(1)}%
                                        </span>
                                      );
                                    })()}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {eacHours != null ? `${Math.round(eacHours).toLocaleString()}h` : '—'}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </>
                          );
                        })()}
                      </Table>
                    </CardContent>
                  </Card>
                </div>

                    </div>
                  )}{/* end Financial Analysis expanded body */}
                </div>{/* end Financial Analysis collapsible */}

                {/* Row 6 — EVM / Earned Value Analysis — hidden for Release 1 */}
                {(false as boolean) && (() => {
                  const completionPct = effectiveCompletionPercent > 0 ? effectiveCompletionPercent / 100 : 0;
                  const bac = enrichedBudgetData?.costBudget?.totals?.budget ?? 0;
                  const actualCost = enrichedBudgetData?.costBudget?.totals?.actual ?? project.actualCosts ?? 0;

                  // Planned Value — proportional to time elapsed through the project
                  const projectStart = project.startDate ? new Date(project.startDate).getTime() : null;
                  const projectEnd = project.endDate ? new Date(project.endDate).getTime() : null;
                  const now = Date.now();
                  const timeElapsedPct = projectStart && projectEnd && projectEnd > projectStart
                    ? Math.min(1, Math.max(0, (now - projectStart) / (projectEnd - projectStart)))
                    : null;
                  const plannedValue = timeElapsedPct !== null ? bac * timeElapsedPct : null;

                  // Earned Value — how much of the budget has been "earned" by work done
                  const earnedValue = bac * completionPct;

                  // Indices
                  const cpi = actualCost > 0 ? earnedValue / actualCost : null;
                  const spi = plannedValue && plannedValue > 0 ? earnedValue / plannedValue : null;

                  // EAC & VAC
                  const eacEvm = cpi && cpi > 0 ? bac / cpi : (completionPct > 0 ? actualCost / completionPct : bac);
                  const vac = bac - eacEvm;

                  // TCPI — To-Complete Performance Index: efficiency needed on remaining work to meet BAC
                  const remainingWork = bac - earnedValue;
                  const remainingBudget = bac - actualCost;
                  const tcpi = remainingWork > 0 && remainingBudget !== 0 ? remainingWork / remainingBudget : null;

                  const noData = bac === 0 || effectiveCompletionPercent === 0;

                  const indexBadge = (value: number | null, label: string, invertGood = false) => {
                    if (value === null) return (
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="text-xs text-muted-foreground uppercase tracking-wide">{label}</span>
                        <span className="text-lg font-semibold text-muted-foreground">—</span>
                      </div>
                    );
                    // > 1.0 = good (green), 0.85–1.0 = amber, < 0.85 = red
                    // invertGood flips the logic (not used here but kept for flexibility)
                    const good = invertGood ? value < 1 : value >= 1.0;
                    const ok = !good && (invertGood ? value <= 1.15 : value >= 0.85);
                    const colour = good ? 'text-success-600' : ok ? 'text-warning-600' : 'text-error-600';
                    const bg = good ? 'bg-success-50' : ok ? 'bg-warning-50' : 'bg-error-50';
                    return (
                      <div className={`flex flex-col items-center gap-0.5 rounded-lg px-4 py-2.5 ${bg}`}>
                        <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium">{label}</span>
                        <span className={`text-xl font-bold ${colour}`}>{value.toFixed(2)}</span>
                        <span className={`text-xs font-medium ${colour}`}>
                          {good ? (label === 'TCPI' ? 'Achievable' : 'On Track') : ok ? 'At Risk' : 'Critical'}
                        </span>
                      </div>
                    );
                  };

                  return (
                    <div className="border rounded-lg overflow-hidden">
                      {/* Header / toggle */}
                      <button
                        type="button"
                        onClick={() => setEvmExpanded((e: boolean) => !e)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-muted/40 hover:bg-muted/60 transition-colors text-left"
                      >
                        <div className="flex items-center gap-2">
                          <Activity className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm font-semibold">Earned Value Analysis</span>
                          <span className="text-xs text-muted-foreground">(EVM — CPI / SPI)</span>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild onClick={e => e.stopPropagation()}>
                                <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-muted text-muted-foreground hover:bg-muted/80 cursor-pointer text-[10px] font-bold leading-none select-none">i</span>
                              </TooltipTrigger>
                              <TooltipContent side="bottom" align="start" className="max-w-xs text-xs space-y-1.5 p-3">
                                <p className="font-semibold text-foreground mb-1">How to read these metrics</p>
                                <p><span className="font-medium text-foreground">CPI &gt; 1.0</span> — Each £1 spent is delivering more than £1 of value. Project is cost-efficient.</p>
                                <p><span className="font-medium text-foreground">CPI &lt; 1.0</span> — Each £1 spent is delivering less than £1 of value. Project will likely overrun.</p>
                                <p><span className="font-medium text-foreground">SPI &gt; 1.0</span> — More work has been completed than was planned for this point in time.</p>
                                <p><span className="font-medium text-foreground">SPI &lt; 1.0</span> — The project is behind schedule relative to the project timeline.</p>
                                <p><span className="font-medium text-foreground">TCPI &gt; 1.0</span> — The remaining work must be delivered more efficiently than it has been to date to stay on budget.</p>
                                <p className="pt-1 italic text-muted-foreground">EVM accuracy depends on % complete being kept up to date. SPI requires project start and end dates.</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          {!noData && cpi !== null && (
                            <span className={`ml-2 text-xs font-semibold px-2 py-0.5 rounded-full ${
                              cpi >= 1.0 ? 'bg-success-100 text-success-700'
                              : cpi >= 0.85 ? 'bg-warning-100 text-warning-700'
                              : 'bg-error-100 text-error-700'
                            }`}>
                              CPI {cpi.toFixed(2)}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          {evmExpanded ? 'Hide' : 'Show'}
                          {evmExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </div>
                      </button>

                      {/* Expanded body */}
                      {evmExpanded && (
                        <div className="px-4 py-4 space-y-5">
                          {noData ? (
                            <p className="text-sm text-muted-foreground text-center py-4">
                              EVM metrics require a cost budget and a % complete value greater than 0.
                            </p>
                          ) : (
                            <>
                              {/* Index row */}
                              <div className="flex flex-wrap gap-3 justify-center">
                                {indexBadge(cpi, 'CPI')}
                                {indexBadge(spi, 'SPI')}
                                {indexBadge(tcpi, 'TCPI')}
                              </div>

                              {/* EVM figures grid */}
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                                {/* BAC */}
                                <div className="rounded-lg border p-3 space-y-0.5">
                                  <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">BAC — Budget at Completion</p>
                                  <p className="text-base font-semibold">{new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(bac)}</p>
                                  <p className="text-xs text-muted-foreground">Authorised total budget</p>
                                </div>
                                {/* EV */}
                                <div className="rounded-lg border p-3 space-y-0.5">
                                  <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">EV — Earned Value</p>
                                  <p className="text-base font-semibold">{new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(earnedValue)}</p>
                                  <p className="text-xs text-muted-foreground">Value of work completed ({effectiveCompletionPercent}%)</p>
                                </div>
                                {/* PV */}
                                <div className="rounded-lg border p-3 space-y-0.5">
                                  <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">PV — Planned Value</p>
                                  <p className="text-base font-semibold">
                                    {plannedValue !== null
                                      ? new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(plannedValue)
                                      : '—'}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {timeElapsedPct !== null ? `${Math.round(timeElapsedPct * 100)}% of project time elapsed` : 'No project dates set'}
                                  </p>
                                </div>
                                {/* AC */}
                                <div className="rounded-lg border p-3 space-y-0.5">
                                  <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">AC — Actual Cost</p>
                                  <p className="text-base font-semibold">{new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(actualCost)}</p>
                                  <p className="text-xs text-muted-foreground">Total cost incurred to date</p>
                                </div>
                                {/* EAC */}
                                <div className="rounded-lg border p-3 space-y-0.5">
                                  <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">EAC — Estimate at Completion</p>
                                  <p className={`text-base font-semibold ${eacEvm > bac ? 'text-error-600' : 'text-success-600'}`}>
                                    {new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(eacEvm)}
                                  </p>
                                  <p className="text-xs text-muted-foreground">Projected final cost (BAC ÷ CPI)</p>
                                </div>
                                {/* VAC */}
                                <div className="rounded-lg border p-3 space-y-0.5">
                                  <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">VAC — Variance at Completion</p>
                                  <p className={`text-base font-semibold ${vac >= 0 ? 'text-success-600' : 'text-error-600'}`}>
                                    {vac >= 0 ? '+' : ''}{new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(vac)}
                                  </p>
                                  <p className="text-xs text-muted-foreground">Projected over / under-run (BAC − EAC)</p>
                                </div>
                                {/* CV */}
                                <div className="rounded-lg border p-3 space-y-0.5">
                                  <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">CV — Cost Variance</p>
                                  {(() => {
                                    const cv = earnedValue - actualCost;
                                    return (
                                      <>
                                        <p className={`text-base font-semibold ${cv >= 0 ? 'text-success-600' : 'text-error-600'}`}>
                                          {cv >= 0 ? '+' : ''}{new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(cv)}
                                        </p>
                                        <p className="text-xs text-muted-foreground">EV − AC (positive = under budget)</p>
                                      </>
                                    );
                                  })()}
                                </div>
                                {/* SV */}
                                <div className="rounded-lg border p-3 space-y-0.5">
                                  <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">SV — Schedule Variance</p>
                                  {(() => {
                                    const sv = plannedValue !== null ? earnedValue - plannedValue : null;
                                    return sv !== null ? (
                                      <>
                                        <p className={`text-base font-semibold ${sv >= 0 ? 'text-success-600' : 'text-error-600'}`}>
                                          {sv >= 0 ? '+' : ''}{new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(sv)}
                                        </p>
                                        <p className="text-xs text-muted-foreground">EV − PV (positive = ahead of plan)</p>
                                      </>
                                    ) : (
                                      <>
                                        <p className="text-base font-semibold text-muted-foreground">—</p>
                                        <p className="text-xs text-muted-foreground">Requires project start/end dates</p>
                                      </>
                                    );
                                  })()}
                                </div>
                              </div>

                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}

    </>
  );
}
